/*
 * HomeCrest Layer 5 - Design submission
 * ===========================================================================
 * A customised design is not bought off the shelf, so it does not go to the
 * cart. It goes to HomeCrest, who confirm feasibility, dimensions, materials
 * and price by hand before anything is made.
 *
 *   design (customised)  ->  payload  ->  channel  ->  human review
 *
 * WhatsApp is the first channel, not the architecture. Channels register the
 * same way providers do in Layers 2 and 4, so a CRM or dashboard drops in later
 * without touching anything here.
 *
 * THE SUBMISSION GATE
 *
 * `canSubmit` requires a preview that is READY and still bound to the CURRENT
 * configuration hash. That is deliberately stricter than "a preview exists":
 *
 *   - a failed generation can never be submitted as though a design were seen
 *   - a stale image can never be submitted for a design it does not depict
 *   - a standard product is sent to the cart, not to a design review
 *
 * The customer is never dead-ended by this. A failed preview offers Try again
 * and Talk to HomeCrest - the second is an ASSISTANCE request, which carries no
 * visualisation and is explicitly not a design submission.
 *
 * The purchase gate lives here too: a customised design disables Add to Cart
 * and Buy It Now, and clearing it restores them. Always reversible, and never
 * applied to a standard configuration.
 */

const QUOTATION_SCHEMA = 'homecrest.submission/2';

const DISCLAIMER =
  'This visualization is an AI-generated design representation of your requested customization. ' +
  'Final construction, dimensions, feasibility, materials and pricing will be confirmed by HomeCrest before production.';

/* ==========================================================================
   Channel registry
   ========================================================================== */

const channels = new Map();
let activeChannel = 'whatsapp';

function registerChannel(name, channel) {
  if (!channel || typeof channel.send !== 'function') throw new TypeError('channel must expose send()');
  channels.set(name, channel);
}

function setChannel(name) {
  if (!channels.has(name)) throw new Error(`unknown channel: ${name}`);
  activeChannel = name;
}

function listChannels() {
  return [...channels.keys()];
}

/* ==========================================================================
   Payload - channel-neutral
   ========================================================================== */

function buildPayload({ configuration, preview = null, product = {}, contact = null, submissionType = 'design' }) {
  return Object.freeze({
    schema: QUOTATION_SCHEMA,
    submission_type: submissionType, // 'design' | 'assistance'
    created_at: new Date().toISOString(),

    design_id: configuration?.design_id ?? null,

    product: {
      id: configuration?.product_id ?? null,
      handle: configuration?.product_handle ?? null,
      title: product.title ?? configuration?.product_title ?? null,
      url: product.url ?? null,
    },
    base_variant: {
      id: configuration?.base_variant_id ?? null,
      title: product.variantTitle ?? null,
    },

    variant_selections: (configuration?.variant_selections ?? []).map((s) => ({ ...s })),

    /* The design, in the order it was built. `source_text` travels here - a
       human reading the request should see what the customer actually typed,
       even though no model ever does. */
    modifications: (configuration?.modifications ?? []).map((m) => ({
      type: m.type,
      instruction: m.instruction,
      axis: m.axis ?? null,
      value: m.value ?? null,
      unit: m.unit ?? null,
      source_text: m.source_text ?? '',
    })),

    configuration_hash: configuration?.configuration_hash ?? null,
    fulfilment: configuration?.fulfilment ?? null,

    preview: preview
      ? {
          status: preview.status,
          image_url: preview.image_url,
          generated_from: preview.generated_from,
          is_representation: preview.is_representation === true,
        }
      : null,

    contact: contact ? { name: contact.name ?? '', phone: contact.phone ?? '', email: contact.email ?? '' } : null,

    disclaimer: DISCLAIMER,
  });
}

/* ==========================================================================
   Gates
   ========================================================================== */

/**
 * May this design be submitted?
 *
 * A visualisation must exist, be READY, and still belong to this exact
 * configuration. Anything less is not a design the customer has actually seen.
 */
function canSubmit(configuration, preview) {
  if (!configuration || configuration.schema !== 'homecrest.configuration/2') {
    return { ok: false, code: 'no_configuration', message: 'Nothing has been designed yet.' };
  }
  if (configuration.fulfilment !== 'customised') {
    return { ok: false, code: 'not_customised', message: 'This is the standard product - add it to the cart instead.' };
  }
  if (!preview) {
    return { ok: false, code: 'no_preview', message: 'Generate a visualization first.' };
  }

  const P = window.HomeCrestPreview;
  if (preview.status === P?.STATES.FAILED) {
    return { ok: false, code: 'preview_failed', message: 'We could not generate this visualization.' };
  }
  if (preview.status === P?.STATES.GENERATING) {
    return { ok: false, code: 'preview_generating', message: 'The visualization is still being generated.' };
  }
  if (!P?.isDisplayable(preview, configuration)) {
    return { ok: false, code: 'stale_preview', message: 'The visualization is out of date. Generate it again.' };
  }
  if (preview.is_representation !== true) {
    return { ok: false, code: 'not_representation', message: 'Preview is not marked representation-only.' };
  }
  return { ok: true };
}

/** Backwards-compatible alias for the previous name. */
const canRequest = canSubmit;

async function send({ configuration, preview = null, product = {}, contact = null, channel = activeChannel, submissionType = 'design' }) {
  /* An ASSISTANCE request is not a design submission: it carries no
     visualisation and is therefore not subject to the preview gate. */
  if (submissionType === 'design') {
    const gate = canSubmit(configuration, preview);
    if (!gate.ok) return { ok: false, error: gate };
  }

  const impl = channels.get(channel);
  if (!impl) return { ok: false, error: { code: 'no_channel', message: `Unknown channel: ${channel}` } };

  const payload = buildPayload({
    configuration,
    preview: submissionType === 'design' ? preview : null,
    product,
    contact,
    submissionType,
  });

  try {
    const result = await impl.send(payload);
    return { ok: true, channel, payload, result };
  } catch (error) {
    return { ok: false, error: { code: 'channel_failed', message: String(error?.message ?? error) }, payload };
  }
}

/* ==========================================================================
   Human-readable rendering, shared by every channel
   ========================================================================== */

function designLines(payload) {
  return payload.modifications.map((m, i) => `${i + 1}. ${m.instruction || `${m.axis}: ${m.value}`}`);
}

function formatLines(payload) {
  const L = [];
  L.push(
    payload.submission_type === 'assistance'
      ? 'HomeCrest Customisation Enquiry'
      : 'HomeCrest Custom Design Request'
  );
  L.push('');
  if (payload.product.title) L.push(`Product: ${payload.product.title}`);
  if (payload.product.url) L.push(`Link: ${payload.product.url}`);
  if (payload.design_id) L.push(`Design ID: ${payload.design_id}`);

  if (payload.modifications.length) {
    L.push('');
    L.push('Your design:');
    designLines(payload).forEach((line) => L.push(line));
  }

  if (payload.variant_selections.length) {
    L.push('');
    L.push(`Base variant: ${payload.variant_selections.map((s) => s.value).join(' / ')}`);
  } else if (payload.base_variant.title) {
    L.push('');
    L.push(`Base variant: ${payload.base_variant.title}`);
  }

  const words = payload.modifications.map((m) => m.source_text).filter(Boolean);
  if (words.length) {
    L.push('');
    L.push('In their words:');
    L.push(words.map((w) => `"${w}"`).join(' · '));
  }

  if (payload.contact) {
    L.push('');
    L.push('Contact:');
    if (payload.contact.name) L.push(`Name: ${payload.contact.name}`);
    if (payload.contact.phone) L.push(`Phone: ${payload.contact.phone}`);
    if (payload.contact.email) L.push(`Email: ${payload.contact.email}`);
  }

  if (payload.preview?.image_url) {
    L.push('');
    L.push('Visualisation:');
    L.push(payload.preview.image_url);
  }

  L.push('');
  L.push(payload.disclaimer);
  return L.join('\n');
}

/* ==========================================================================
   WhatsApp channel
   --------------------------------------------------------------------------
   wa.me is a public deep link, not an API - there is no credential involved
   and nothing is sent from the page. It opens the customer's own WhatsApp with
   the message pre-composed; the customer presses send.
   ========================================================================== */

registerChannel('whatsapp', {
  name: 'whatsapp',
  send(payload) {
    const number = String(window.HomeCrestQuotationConfig?.whatsappNumber ?? '').replace(/[^\d]/g, '');
    if (!number) throw new Error('No WhatsApp number configured.');

    const url = `https://wa.me/${number}?text=${encodeURIComponent(formatLines(payload))}`;
    window.open(url, '_blank', 'noopener');
    return { opened: true, url };
  },
});

/* ==========================================================================
   Callback channel - Shopify's own contact form
   --------------------------------------------------------------------------
   No new infrastructure and no third party: the customer's details go straight
   into the store's own contact pipeline, which is where the rest of HomeCrest's
   customer correspondence already lives.
   ========================================================================== */

registerChannel('contact', {
  name: 'contact',
  async send(payload) {
    const c = payload.contact ?? {};
    if (!c.name || !c.phone) throw new Error('A name and phone number are required.');

    const body = new URLSearchParams({
      form_type: 'contact',
      utf8: '✓',
      'contact[name]': c.name,
      'contact[phone]': c.phone,
      'contact[email]': c.email || '',
      'contact[body]': formatLines(payload),
    });

    const response = await fetch('/contact', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok && response.type !== 'opaqueredirect') {
      throw new Error(`Contact form returned ${response.status}.`);
    }
    return { submitted: true };
  },
});

/* A channel that only formats, for testing and for future CRM reuse. */
registerChannel('inspect', {
  name: 'inspect',
  send(payload) {
    return { text: formatLines(payload), payload };
  },
});

/* ==========================================================================
   Purchase gate
   ========================================================================== */

function applyPurchaseGate(configuration, root = document) {
  const scope = root.querySelector?.('.hc-pdp-editorial') ?? root;
  const atc = scope.querySelector('.add-to-cart-button');
  const buyNow = scope.querySelector('.accelerated-checkout-block');
  const customised = configuration?.fulfilment === 'customised';

  if (atc) {
    if (customised) {
      if (!atc.hasAttribute('data-hc-gated')) {
        atc.setAttribute('data-hc-gated', 'true');
        atc.dataset.hcPrevDisabled = atc.disabled ? 'true' : 'false';
      }
      atc.disabled = true;
    } else if (atc.hasAttribute('data-hc-gated')) {
      atc.disabled = atc.dataset.hcPrevDisabled === 'true';
      atc.removeAttribute('data-hc-gated');
      delete atc.dataset.hcPrevDisabled;
    }
  }

  if (buyNow) {
    if (customised) {
      buyNow.setAttribute('data-hc-gated', 'true');
      buyNow.setAttribute('aria-hidden', 'true');
    } else {
      buyNow.removeAttribute('data-hc-gated');
      buyNow.removeAttribute('aria-hidden');
    }
  }

  return { gated: customised };
}

function clearPurchaseGate(root = document) {
  return applyPurchaseGate(null, root);
}

window.HomeCrestQuotation = {
  SCHEMA: QUOTATION_SCHEMA,
  DISCLAIMER,
  registerChannel,
  setChannel,
  listChannels,
  buildPayload,
  formatLines,
  designLines,
  canSubmit,
  canRequest,
  send,
  applyPurchaseGate,
  clearPurchaseGate,
};
